import type { BoardDocument, DeviceProfile, StudioBootstrap } from "@contracts";

export class ApiError extends Error {
  constructor(public readonly status: number, message: string, public readonly details?: unknown) {
    super(message);
  }
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: response.statusText }));
    throw new ApiError(response.status, payload.error ?? response.statusText, payload.details);
  }
  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) return response.json() as Promise<T>;
  return response.blob() as Promise<T>;
}

export const api = {
  bootstrap: (boardId?: string) => request<StudioBootstrap>(`/api/v1/studio/bootstrap${boardId ? `?boardId=${encodeURIComponent(boardId)}` : ""}`),
  createBoard: (name: string, deviceProfileId: string, cloneBoardId?: string) => request<{ board: BoardDocument }>("/api/v1/boards", { method: "POST", body: JSON.stringify({ name, deviceProfileId, cloneBoardId }) }),
  login: (username: string, password: string) => request<{ user: { username: string } }>("/api/v1/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  }),
  logout: () => request<{ ok: true }>("/api/v1/auth/logout", { method: "POST", body: "{}" }),
  saveBoard: (board: BoardDocument) => request<BoardDocument>(`/api/v1/boards/${board.id}`, { method: "PUT", body: JSON.stringify(board) }),
  publish: (boardId: string, targetId?: string) => request<Record<string, unknown>>(`/api/v1/boards/${boardId}/releases`, {
    method: "POST",
    body: JSON.stringify({ targetId, render: true }),
  }),
  renderTarget: (targetId: string) => request<Record<string, unknown>>(`/api/v1/targets/${targetId}/render`, { method: "POST", body: JSON.stringify({ forceSources: true }) }),
  preview: async (board: BoardDocument, variantId: string, fixtureState?: string) => request<Blob>(`/api/v1/boards/${board.id}/preview`, {
    method: "POST",
    body: JSON.stringify({ document: board, variantId, fixtureState }),
  }),
  refreshSources: () => request<Record<string, unknown>>("/api/v1/snapshots/refresh", { method: "POST", body: JSON.stringify({ force: true }) }),
  updateSource: (id: string, patch: Record<string, unknown>) => request<Record<string, unknown>>(`/api/v1/sources/instances/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  saveDevice: (profile: DeviceProfile) => request<DeviceProfile>("/api/v1/device-profiles", { method: "POST", body: JSON.stringify(profile) }),
  uploadAsset: (file: File, name?: string, tags?: string) => {
    const data = new FormData();
    data.append("file", file);
    if (name) data.append("name", name);
    if (tags) data.append("tags", tags);
    return request<Record<string, unknown>>("/api/v1/assets", { method: "POST", body: data });
  },
  assetReferences: (id: string) => request<{ boards: unknown[]; releases: string[] }>(`/api/v1/assets/${id}/references`),
  deleteAsset: (id: string) => request<void>(`/api/v1/assets/${id}`, { method: "DELETE" }),
  rollback: (releaseId: string, targetId: string) => request<Record<string, unknown>>(`/api/v1/releases/${releaseId}/rollback`, { method: "POST", body: JSON.stringify({ targetId }) }),
  exportScreenPackUrl: (boardId: string, includeRestricted = false) => `/api/v1/boards/${boardId}/screenpack?includeRestricted=${includeRestricted}`,
  importScreenPack: (file: File) => {
    const data = new FormData();
    data.append("file", file);
    return request<BoardDocument>("/api/v1/screenpacks/import", { method: "POST", body: data });
  },
};
